const SITE_URL = "https://getblackride.com";

const DEFAULT_ORGANIZATION = {
  "@type": "Organization",
  "@id": `${SITE_URL}#organization`,
  name: "Blackride Establishment",
  alternateName: "BlackRide",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  telephone: "+966540423401",
  email: "hello@getblackride.com",
  slogan: "Premium chauffeur booking platform in Saudi Arabia",
  sameAs: [
    "https://www.instagram.com/getblackride/",
    "https://www.linkedin.com/company/getblackride/",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "7644, Abu Al Saud Al Zaini Al Qastalani Street, Al Hada Dist",
    addressLocality: "Jeddah",
    addressRegion: "Makkah Province",
    addressCountry: "SA",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+966540423401",
      contactType: "customer service",
      areaServed: ["SA", "AE", "QA", "KW", "OM", "BH"],
      availableLanguage: ["English", "Arabic"],
      url: "https://wa.me/966540423401",
    },
  ],
};

export function absoluteUrl(pathOrUrl = "/") {
  if (!pathOrUrl) return SITE_URL;
  if (pathOrUrl.startsWith("http")) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

export function buildOrganizationSchema(overrides = {}) {
  return {
    ...DEFAULT_ORGANIZATION,
    ...overrides,
  };
}

export function buildLocalBusinessSchema({
  name = "Blackride",
  description,
  url = SITE_URL,
  image = `${SITE_URL}/logo.png`,
  telephone = "+966540423401",
  areaServed = ["Jeddah", "Makkah", "Madinah", "Riyadh", "Dammam", "Saudi Arabia"],
  sameAs = DEFAULT_ORGANIZATION.sameAs,
} = {}) {
  const servedPlaces = areaServed.map((area) => ({ "@type": "Place", name: area }));
  return {
    "@type": "LocalBusiness",
    "@id": `${absoluteUrl(url)}#localbusiness`,
    name,
    image,
    url,
    telephone,
    email: "hello@getblackride.com",
    description,
    priceRange: "$$$",
    currenciesAccepted: "SAR",
    availableLanguage: ["English", "Arabic"],
    address: DEFAULT_ORGANIZATION.address,
    legalName: "Blackride Establishment",
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    ],
    sameAs,
    contactPoint: DEFAULT_ORGANIZATION.contactPoint,
    areaServed: servedPlaces,
    serviceArea: servedPlaces,
    knowsAbout: [
      "Airport transfer in Saudi Arabia",
      "Jeddah Airport to Makkah transfer",
      "Makkah to Madinah private transfer",
      "Corporate chauffeur service",
      "VIP transportation",
      "Umrah transportation",
      "Hotel transfers",
      "Intercity chauffeur service",
      "Airport meet-and-greet",
      "Luxury chauffeur service",
    ],
  };
}

export function buildServiceSchema({
  name,
  description,
  url,
  areaServed = ["Saudi Arabia"],
  serviceType,
  audience,
}) {
  const servedPlaces = areaServed.map((area) => ({ "@type": "Place", name: area }));
  return {
    "@type": "Service",
    "@id": `${absoluteUrl(url)}#service`,
    name,
    description,
    url,
    serviceType: serviceType || name,
    provider: {
      "@id": `${SITE_URL}#organization`,
    },
    areaServed: servedPlaces,
    serviceArea: servedPlaces,
    audience: audience ? { "@type": "Audience", audienceType: audience } : undefined,
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: "https://wa.me/966540423401",
      availableLanguage: ["English", "Arabic"],
    },
    hoursAvailable: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "00:00",
      closes: "23:59",
    },
  };
}

export function buildFaqSchema(faqs = []) {
  if (!faqs.length) return null;
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function buildBreadcrumbSchema(items = []) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.item),
    })),
  };
}

export function buildBlogPostingSchema({
  headline,
  description,
  url,
  image = `${SITE_URL}/logo.png`,
  datePublished,
  dateModified,
  keywords = [],
  authorName = "BlackRide Editorial Team",
}) {
  return {
    "@type": "BlogPosting",
    headline,
    description,
    mainEntityOfPage: absoluteUrl(url),
    image,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      "@type": "Organization",
      name: authorName,
    },
    publisher: {
      "@type": "Organization",
      name: "BlackRide",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`,
      },
    },
    keywords: keywords.join(", "),
  };
}

export function buildProductSchemas(fleet = [], url) {
  return fleet.map((item) => ({
    "@type": "Product",
    name: `${item.category} - ${item.name}`,
    description: item.description,
    image: absoluteUrl(item.image),
    brand: {
      "@type": "Brand",
      name: "BlackRide",
    },
    category: item.category,
    url: absoluteUrl(url),
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Seats",
        value: item.seats,
      },
      {
        "@type": "PropertyValue",
        name: "Luggage",
        value: item.luggage,
      },
    ],
  }));
}

export function buildPageSchemaGraph(items = []) {
  return {
    "@context": "https://schema.org",
    "@graph": items.filter(Boolean),
  };
}
